import React from 'react';
import { MapPin, CreditCard, Download } from 'lucide-react';
import { Button } from './ui/button';

export const HowItWorks = () => {
  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Decorative clouds */}
      <div className="absolute top-10 right-10 w-32 h-8 bg-white rounded-full opacity-60"></div>
      <div className="absolute bottom-20 left-10 w-24 h-6 bg-white rounded-full opacity-60"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 rounded-full text-sm font-medium mb-4">
            Simple & Fast Process
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get your flight reservation in just 3 easy steps
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Step 1 */}
          <div className="relative group">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-blue-500">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  01
                </div>
              </div>
              
              <div className="mt-6 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <MapPin className="text-blue-600" size={40} />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Enter Your Route
              </h3>
              <p className="text-gray-600 text-center">
                Specify your origin, destination, travel dates and passenger details. Choose one-way or round-trip based on your needs.
              </p>
            </div>
            {/* Connector line - hidden on mobile */}
            <div className="hidden md:block absolute top-1/3 left-full w-full h-1 bg-gradient-to-r from-blue-300 to-purple-300 -z-10"></div>
          </div>

          {/* Step 2 */}
          <div className="relative group">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-purple-500">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  02
                </div>
              </div>
              
              <div className="mt-6 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <CreditCard className="text-purple-600" size={40} />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Contact & Confirm
              </h3>
              <p className="text-gray-600 text-center">
                Connect with us via WhatsApp with your booking details. We'll create a real reservation with PNR code instantly.
              </p>
            </div>
            {/* Connector line - hidden on mobile */}
            <div className="hidden md:block absolute top-1/3 left-full w-full h-1 bg-gradient-to-r from-purple-300 to-green-300 -z-10"></div>
          </div>

          {/* Step 3 */}
          <div className="relative group">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-green-500">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  03
                </div>
              </div>
              
              <div className="mt-6 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Download className="text-green-600" size={40} />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Get Your Ticket
              </h3>
              <p className="text-gray-600 text-center">
                Receive your flight reservation via WhatsApp or email within 2 minutes. Verify on airline's website for peace of mind!
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={scrollToBooking}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-6 text-lg font-bold rounded-xl shadow-2xl hover:shadow-purple-500/50 transition-all transform hover:scale-105"
          >
            Start Your Booking Now
          </Button>
          <p className="mt-4 text-sm text-gray-600">
            🔒 Secure process • ⚡ Instant delivery • 💯 Money-back guarantee
          </p>
        </div>
      </div>
    </section>
  );
};
